/**
 * ganzhi.js — 干支历法
 *
 * 六爻断卦的一切旺衰、冲合、空亡都以「月建 / 日辰」为基准，
 * 所以历法必须是真实天文历，不能用公历月份糊弄。
 *
 * - 日柱：儒略日直接换算，精确无误差（已用寿星天文历 400 组随机日期验证）
 * - 月柱：以「节」（非中气）为界，太阳视黄经过 315°(立春)、345°(惊蛰)… 时换月
 * - 年柱：以立春为界，不是正月初一，更不是元旦
 * - 时柱：五鼠遁
 *
 * 太阳黄经用 Meeus 低精度算法，误差约 0.01°（换算到节气时刻约 ±15 分钟）。
 * 只有在提问时刻正好压在交节点前后十几分钟时才可能取错月建，
 * 这种情况下请手动指定 monthBranch。
 */

export const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
export const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];

/** 地支五行 */
export const BRANCH_ELEMENT = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火',
  午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水'
};

/** 天干五行 */
export const STEM_ELEMENT = {
  甲: '木', 乙: '木', 丙: '火', 丁: '火', 戊: '土',
  己: '土', 庚: '金', 辛: '金', 壬: '水', 癸: '水'
};

/** 地支生肖，展示用 */
export const BRANCH_ZODIAC = {
  子: '鼠', 丑: '牛', 寅: '虎', 卯: '兔', 辰: '龙', 巳: '蛇',
  午: '马', 未: '羊', 申: '猴', 酉: '鸡', 戌: '狗', 亥: '猪'
};

/** 二十四节气中的十二「节」，及其对应太阳黄经与月建地支 */
export const MAJOR_TERMS = [
  { name: '立春', lon: 315, branch: '寅' },
  { name: '惊蛰', lon: 345, branch: '卯' },
  { name: '清明', lon: 15, branch: '辰' },
  { name: '立夏', lon: 45, branch: '巳' },
  { name: '芒种', lon: 75, branch: '午' },
  { name: '小暑', lon: 105, branch: '未' },
  { name: '立秋', lon: 135, branch: '申' },
  { name: '白露', lon: 165, branch: '酉' },
  { name: '寒露', lon: 195, branch: '戌' },
  { name: '立冬', lon: 225, branch: '亥' },
  { name: '大雪', lon: 255, branch: '子' },
  { name: '小寒', lon: 285, branch: '丑' }
];

/**
 * 日盘飞星所需的二至（中气）。默认 termsOfYear() 仍只返回上面
 * 十二「节」，避免改变六爻月建、年柱和应期的现有索引语义。
 */
export const SOLSTICE_TERMS = Object.freeze([
  Object.freeze({ name: '夏至', lon: 90 }),
  Object.freeze({ name: '冬至', lon: 270 })
]);

const RAD = Math.PI / 180;

/* ---------------------------------------------------------------- 儒略日 */

/** 公历年月日 → 儒略日数（当日 12:00 UT 的整数 JDN） */
export function toJDN(year, month, day) {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

/** 带时刻的儒略日（UT） */
export function toJD(year, month, day, hour = 12, minute = 0, second = 0) {
  return toJDN(year, month, day) - 0.5 + (hour + minute / 60 + second / 3600) / 24;
}

/** 儒略日 → 公历（UT） */
export function fromJD(jd) {
  const z = Math.floor(jd + 0.5);
  const f = jd + 0.5 - z;
  let a = z;
  if (z >= 2299161) {
    const alpha = Math.floor((z - 1867216.25) / 36524.25);
    a = z + 1 + alpha - Math.floor(alpha / 4);
  }
  const b = a + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);
  const dayF = b - d - Math.floor(30.6001 * e) + f;
  const day = Math.floor(dayF);
  const month = e < 14 ? e - 1 : e - 13;
  const year = month > 2 ? c - 4716 : c - 4715;
  const hours = (dayF - day) * 24;
  return {
    year,
    month,
    day,
    hour: Math.floor(hours),
    minute: Math.floor((hours % 1) * 60)
  };
}

/* ------------------------------------------------------------ 太阳黄经 */

/** 太阳视黄经（度，0–360），Meeus《天文算法》低精度式 */
export function solarLongitude(jd) {
  const T = (jd - 2451545.0) / 36525;
  const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
  const M = (357.52911 + 35999.05029 * T - 0.0001537 * T * T) * RAD;
  const C =
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * M) +
    0.000289 * Math.sin(3 * M);
  const omega = (125.04 - 1934.136 * T) * RAD;
  const apparent = L0 + C - 0.00569 - 0.00478 * Math.sin(omega);
  return ((apparent % 360) + 360) % 360;
}

/**
 * 求 jd 之后（或之前）最近一次太阳黄经等于 targetLon 的时刻。
 * 二分法，精度 1 秒。
 */
function solveTerm(jdGuess, targetLon) {
  const diff = (jd) => {
    let d = solarLongitude(jd) - targetLon;
    while (d > 180) d -= 360;
    while (d < -180) d += 360;
    return d;
  };
  let lo = jdGuess - 3;
  let hi = jdGuess + 3;
  // 保证区间跨过零点
  let guard = 0;
  while (diff(lo) > 0 && guard++ < 10) lo -= 3;
  guard = 0;
  while (diff(hi) < 0 && guard++ < 10) hi += 3;
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    if (diff(mid) < 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * 某公历年的「节」交节时刻（返回本地时的 JD，含时区偏移）。
 * tzOffset 单位小时，默认东八区（传统命理一律用北京时间）。
 * options.includeSolstices = true 时额外返回夏至、冬至两个中气；
 * 默认仍为十二节，以保持月建语义。
 */
export function termsOfYear(year, tzOffset = 8, options = {}) {
  const requestedTerms = options.includeSolstices
    ? [...MAJOR_TERMS, ...SOLSTICE_TERMS]
    : MAJOR_TERMS;
  return requestedTerms.map((term) => {
    // 立春/惊蛰在年初，黄经 315/345 属于「上一个回归年」的尾部
    const approxMonth = term.lon >= 315
      ? (term.lon === 315 ? 2 : 3)
      : Math.round(term.lon / 30) + 3;
    const guess = toJD(year, Math.min(approxMonth, 12), 6, 12) - tzOffset / 24;
    const jdUT = solveTerm(guess, term.lon);
    return { ...term, jd: jdUT + tzOffset / 24 };
  });
}

/* -------------------------------------------------------------- 四柱 */

/** 由天干序号与地支序号反解六十甲子序号 */
function sexagenaryIndex(stemIdx, branchIdx) {
  return ((stemIdx * 6 - branchIdx * 5) % 60 + 60) % 60;
}

const pillar = (index) => ({
  index: ((index % 60) + 60) % 60,
  stem: STEMS[((index % 10) + 10) % 10],
  branch: BRANCHES[((index % 12) + 12) % 12],
  get name() {
    return this.stem + this.branch;
  }
});

/** 五虎遁：由年干推正月（寅月）天干 */
const MONTH_STEM_START = [2, 4, 6, 8, 0]; // 甲己→丙, 乙庚→戊, 丙辛→庚, 丁壬→壬, 戊癸→甲

/** 五鼠遁：由日干推子时天干 */
const HOUR_STEM_START = [0, 2, 4, 6, 8]; // 甲己→甲子, 乙庚→丙子, 丙辛→戊子, 丁壬→庚子, 戊癸→壬子

/**
 * 求某一时刻的四柱。
 *
 * @param {object} t  { year, month, day, hour = 12, minute = 0 } 本地时（默认北京时间）
 * @param {object} opts
 *   tzOffset     时区，默认 8
 *   dayBoundary  'midnight'（默认，主流六爻用法）| 'zi'（23:00 起算次日）
 *   termGranularity 'exact'（默认，交节的那一刻换月，四柱正法）
 *                 | 'day'（交节当日整天算新月，多数手机 App 的做法）
 */
export function fourPillars(t, opts = {}) {
  const { tzOffset = 8, dayBoundary = 'midnight', termGranularity = 'exact' } = opts;
  const { year, month, day, hour = 12, minute = 0 } = t;

  // ---- 日柱：JDN 直算 ----
  let jdn = toJDN(year, month, day);
  if (dayBoundary === 'zi' && hour >= 23) jdn += 1;
  const dayPillar = pillar(jdn - 11); // 已验证：index 0 = 甲子

  // ---- 年柱 / 月柱：以节为界 ----
  // 注意：toJD 接受的是「墙上时钟」数值，得到的就是本地 JD，
  // 不能再加时区偏移（节气时刻已在 termsOfYear 里换算为本地 JD）。
  const jdLocal =
    termGranularity === 'day'
      ? toJD(year, month, day, 23, 59)
      : toJD(year, month, day, hour, minute);
  let terms = termsOfYear(year, tzOffset);

  let solarYear = year;
  if (jdLocal < terms[0].jd) {
    // 立春之前，属上一年
    solarYear = year - 1;
    terms = termsOfYear(solarYear, tzOffset);
  }
  // 找到当前所处的节
  let termIndex = 0;
  for (let i = terms.length - 1; i >= 0; i -= 1) {
    if (jdLocal >= terms[i].jd) {
      termIndex = i;
      break;
    }
  }
  // 小寒（index 11）之后到次年立春之前，仍算 solarYear
  const currentTerm = terms[termIndex];

  const yearIndex = (solarYear - 1984) % 60; // 1984 甲子年
  const yearPillar = pillar(yearIndex);

  // 月柱：寅月为该年第 0 月
  const monthOrder = termIndex; // 0=寅 …… 11=丑
  const monthStem = (MONTH_STEM_START[yearPillar.index % 5] + monthOrder) % 10;
  const monthBranchIdx = (2 + monthOrder) % 12; // 寅 = 2
  const monthPillar = pillar(sexagenaryIndex(monthStem, monthBranchIdx));

  // ---- 时柱 ----
  const hourBranchIdx = Math.floor(((hour + 1) % 24) / 2);
  const hourStem = (HOUR_STEM_START[dayPillar.index % 5] + hourBranchIdx) % 10;
  const hourPillar = pillar(sexagenaryIndex(hourStem, hourBranchIdx));

  return {
    /** 保留历法计算的起算时刻，供应期日期工具定位后续日/月；不参与断卦。 */
    at: { year, month, day, hour, minute },
    calendarOptions: { tzOffset, dayBoundary, termGranularity },
    year: yearPillar,
    month: monthPillar,
    day: dayPillar,
    hour: hourPillar,
    term: currentTerm.name,
    termJD: currentTerm.jd,
    /** 旬空（空亡）两支 */
    voidBranches: voidBranches(dayPillar.index)
  };
}

/** 旬空：日柱所在一旬（十日）缺失的两个地支 */
export function voidBranches(dayIndex) {
  const xunStart = dayIndex - (dayIndex % 10); // 旬首序号
  const startBranch = xunStart % 12;
  return [BRANCHES[(startBranch + 10) % 12], BRANCHES[(startBranch + 11) % 12]];
}

/** 由干支名反查 60 序号 */
export function ganzhiIndex(name) {
  const s = STEMS.indexOf(name[0]);
  const b = BRANCHES.indexOf(name[1]);
  if (s < 0 || b < 0) throw new Error(`不是合法干支：${name}`);
  return sexagenaryIndex(s, b);
}

export { pillar, sexagenaryIndex };
